// Footer Settings Management - Contact Info & Social Media
import React, { useState, useEffect } from 'react';
import { Save, Mail, Phone, MapPin, MessageCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

const FooterSettingsManagement = () => {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/settings/`);
      const data = await response.json();
      setSettings(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/settings/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(settings),
      });
      if (response.ok) {
        alert('تم حفظ إعدادات الفوتر بنجاح ✅');
      }
    } catch (error) {
      alert('حدث خطأ في الحفظ ❌');
    }
  };

  const updateContactSettings = (key, value) => {
    setSettings({
      ...settings,
      contact_settings: {
        ...settings.contact_settings,
        [key]: value
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F388E]"></div>
          <p className="mt-4 text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  const contactSettings = settings?.contact_settings || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-[#0F388E]">إعدادات الفوتر</h1>
        <p className="text-gray-600">التحكم في معلومات التواصل ومواقع التواصل الاجتماعي</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Email Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-6 h-6 text-[#0F388E]" />
              البريد الإلكتروني
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  البريد الإلكتروني الرئيسي *
                </label>
                <input
                  type="email"
                  value={contactSettings.email || ''}
                  onChange={(e) => updateContactSettings('email', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  البريد الإلكتروني الثانوي
                </label>
                <input
                  type="email"
                  value={contactSettings.email_secondary || ''}
                  onChange={(e) => updateContactSettings('email_secondary', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Phone Numbers Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Phone className="w-6 h-6 text-[#0F388E]" />
              أرقام الهاتف
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  رقم الهاتف الرئيسي *
                </label>
                <input
                  type="text"
                  value={contactSettings.phone || ''}
                  onChange={(e) => updateContactSettings('phone', e.target.value)}
                  placeholder="+967 1 234567"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  رقم الهاتف الثانوي
                </label>
                <input
                  type="text"
                  value={contactSettings.phone_secondary || ''}
                  onChange={(e) => updateContactSettings('phone_secondary', e.target.value)}
                  placeholder="+967 1 234567"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  رقم الجوال
                </label>
                <input
                  type="text"
                  value={contactSettings.mobile || ''}
                  onChange={(e) => updateContactSettings('mobile', e.target.value)}
                  placeholder="+967 777 123456"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  رابط الواتساب
                </label>
                <input
                  type="url"
                  value={contactSettings.whatsapp || ''}
                  onChange={(e) => updateContactSettings('whatsapp', e.target.value)}
                  placeholder="https://wa.me/967777123456"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
                <p className="text-xs text-gray-500 mt-1">مثال: https://wa.me/967777123456</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  رقم الفاكس
                </label>
                <input
                  type="text"
                  value={contactSettings.fax || ''}
                  onChange={(e) => updateContactSettings('fax', e.target.value)}
                  placeholder="+967 1 234567"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Address Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-6 h-6 text-[#0F388E]" />
              العنوان
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  العنوان بالعربي *
                </label>
                <input
                  type="text"
                  value={contactSettings.address_ar || ''}
                  onChange={(e) => updateContactSettings('address_ar', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  العنوان بالإنجليزي
                </label>
                <input
                  type="text"
                  value={contactSettings.address_en || ''}
                  onChange={(e) => updateContactSettings('address_en', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  العنوان المفصل بالعربي
                </label>
                <textarea
                  value={contactSettings.address_full_ar || ''}
                  onChange={(e) => updateContactSettings('address_full_ar', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  العنوان المفصل بالإنجليزي
                </label>
                <textarea
                  value={contactSettings.address_full_en || ''}
                  onChange={(e) => updateContactSettings('address_full_en', e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Social Media Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-[#0F388E]" />
              مواقع التواصل الاجتماعي
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <span className="text-blue-600">📘</span> Facebook
                </label>
                <input
                  type="url"
                  value={contactSettings.facebook || ''}
                  onChange={(e) => updateContactSettings('facebook', e.target.value)}
                  placeholder="https://facebook.com/..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <span className="text-sky-500">🐦</span> Twitter / X
                </label>
                <input
                  type="url"
                  value={contactSettings.twitter || ''}
                  onChange={(e) => updateContactSettings('twitter', e.target.value)}
                  placeholder="https://twitter.com/..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <span className="text-pink-600">📷</span> Instagram
                </label>
                <input
                  type="url"
                  value={contactSettings.instagram || ''}
                  onChange={(e) => updateContactSettings('instagram', e.target.value)}
                  placeholder="https://instagram.com/..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <span className="text-red-600">▶️</span> YouTube
                </label>
                <input
                  type="url"
                  value={contactSettings.youtube || ''}
                  onChange={(e) => updateContactSettings('youtube', e.target.value)}
                  placeholder="https://youtube.com/..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <span className="text-blue-700">💼</span> LinkedIn
                </label>
                <input
                  type="url"
                  value={contactSettings.linkedin || ''}
                  onChange={(e) => updateContactSettings('linkedin', e.target.value)}
                  placeholder="https://linkedin.com/..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <span className="text-sky-600">✈️</span> Telegram
                </label>
                <input
                  type="url"
                  value={contactSettings.telegram || ''}
                  onChange={(e) => updateContactSettings('telegram', e.target.value)}
                  placeholder="https://t.me/..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <span>🎵</span> TikTok
                </label>
                <input
                  type="url"
                  value={contactSettings.tiktok || ''}
                  onChange={(e) => updateContactSettings('tiktok', e.target.value)}
                  placeholder="https://tiktok.com/@..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Map Coordinates Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-6 h-6 text-[#0F388E]" />
              إحداثيات الخريطة (Google Maps)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-4">
              <p className="text-sm text-blue-800">
                💡 <strong>نصيحة:</strong> للحصول على الإحداثيات، افتح Google Maps، اضغط بزر الماوس الأيمن على موقع الجامعة، واختر الرقم الأول (خط العرض) والثاني (خط الطول).
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                رابط خرائط Google المباشر (Google Maps URL)
              </label>
              <input
                type="url"
                value={contactSettings.map_url || ''}
                onChange={(e) => updateContactSettings('map_url', e.target.value)}
                placeholder="https://maps.app.goo.gl/..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
              />
              <p className="text-xs text-gray-500 mt-1">
                الرابط الذي يتم فتحه مباشرة عند النقر على العنوان أو زر الخريطة في الفوتر وصفحة اتصل بنا
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  خط العرض (Latitude)
                </label>
                <input
                  type="text"
                  value={contactSettings.map_latitude || ''}
                  onChange={(e) => updateContactSettings('map_latitude', e.target.value)}
                  placeholder="15.369445"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  خط الطول (Longitude)
                </label>
                <input
                  type="text"
                  value={contactSettings.map_longitude || ''}
                  onChange={(e) => updateContactSettings('map_longitude', e.target.value)}
                  placeholder="44.191006"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  مستوى التكبير (Zoom)
                </label>
                <input
                  type="number"
                  value={contactSettings.map_zoom || '15'}
                  onChange={(e) => updateContactSettings('map_zoom', e.target.value)}
                  min="1"
                  max="20"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer Text Section */}
        <Card>
          <CardHeader>
            <CardTitle>نص الفوتر وحقوق النشر</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  نبذة في الفوتر (عربي)
                </label>
                <textarea
                  value={contactSettings.footer_about_ar || ''}
                  onChange={(e) => updateContactSettings('footer_about_ar', e.target.value)}
                  rows={4}
                  placeholder="نبذة مختصرة عن الجامعة تظهر في الفوتر"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  نبذة في الفوتر (إنجليزي)
                </label>
                <textarea
                  value={contactSettings.footer_about_en || ''}
                  onChange={(e) => updateContactSettings('footer_about_en', e.target.value)}
                  rows={4}
                  placeholder="Brief about the university shown in footer"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  نص حقوق النشر (عربي)
                </label>
                <input
                  type="text"
                  value={contactSettings.copyright_text_ar || ''}
                  onChange={(e) => updateContactSettings('copyright_text_ar', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  نص حقوق النشر (إنجليزي)
                </label>
                <input
                  type="text"
                  value={contactSettings.copyright_text_en || ''}
                  onChange={(e) => updateContactSettings('copyright_text_en', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Custom Footer Links */}
        <Card>
          <CardHeader>
            <CardTitle>🔗 روابط مخصصة في الفوتر</CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              ستظهر في قسم "روابط مهمة" في الفوتر بدلاً من البرامج الدراسية
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Add New Link Form */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-gray-700 mb-3">إضافة رابط جديد</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  type="text"
                  id="new-link-name-ar"
                  placeholder="اسم الرابط بالعربية"
                  className="px-4 py-2 border rounded-lg"
                />
                <input
                  type="text"
                  id="new-link-name-en"
                  placeholder="Link Name (Optional)"
                  className="px-4 py-2 border rounded-lg"
                />
                <input
                  type="url"
                  id="new-link-url"
                  placeholder="https://... أو /page"
                  className="px-4 py-2 border rounded-lg"
                />
              </div>
              <Button
                type="button"
                onClick={() => {
                  const nameAr = document.getElementById('new-link-name-ar').value;
                  const nameEn = document.getElementById('new-link-name-en').value;
                  const url = document.getElementById('new-link-url').value;
                  
                  if (nameAr && url) {
                    const newLink = { name_ar: nameAr, name_en: nameEn, url: url };
                    const currentLinks = contactSettings.custom_links || [];
                    updateContactSettings('custom_links', [...currentLinks, newLink]);
                    
                    // Clear inputs
                    document.getElementById('new-link-name-ar').value = '';
                    document.getElementById('new-link-name-en').value = '';
                    document.getElementById('new-link-url').value = '';
                  } else {
                    alert('يرجى إدخال اسم الرابط والرابط على الأقل');
                  }
                }}
                className="mt-3 bg-blue-600 hover:bg-blue-700"
              >
                إضافة الرابط
              </Button>
            </div>

            {/* List of Custom Links */}
            {contactSettings.custom_links && contactSettings.custom_links.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold text-gray-700">الروابط المضافة:</h4>
                {contactSettings.custom_links.map((link, index) => (
                  <div key={index} className="flex items-center gap-3 bg-white p-3 rounded-lg border">
                    <div className="flex-1">
                      <p className="font-semibold text-[#0F388E]">{link.name_ar}</p>
                      {link.name_en && (
                        <p className="text-sm text-gray-600">{link.name_en}</p>
                      )}
                      <a 
                        href={link.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline"
                      >
                        {link.url}
                      </a>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const newLinks = contactSettings.custom_links.filter((_, i) => i !== index);
                        updateContactSettings('custom_links', newLinks);
                      }}
                      className="text-red-500 hover:text-red-700 transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {(!contactSettings.custom_links || contactSettings.custom_links.length === 0) && (
              <p className="text-sm text-gray-500 text-center py-4">
                لم تتم إضافة روابط مخصصة بعد. سيتم عرض الروابط الافتراضية في الفوتر.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button type="submit" className="bg-[#0F388E] px-8">
            <Save className="ml-2 w-5 h-5" />
            حفظ إعدادات الفوتر
          </Button>
        </div>
      </form>
    </div>
  );
};

export default FooterSettingsManagement;
